import { Grid, Typography } from '@mui/material';
import { ActionButton } from '@tassat/button';
import {
    ActionTypes,
    AdminRoleId,
    SecondaryApiTypeEnum,
    doesWorkflowRejectCodeExist,
    getCountryOptions,
    getStateProvinceOptions
} from '@tassat/common-definitions';
import { FirmForm } from '@tassat/forms';
import { CustomerModifyFirmReq, FirmRecord } from '@tassat/fred-definitions';
import {
    Edit as EditIcon,
    Lock as LockIcon,
    Unlock as UnlockIcon,
    Wallet as WalletIcon
} from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import { AssetSymbol, ClosedSymbol } from '@tassat/symbols';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import {
    getCollateral, getTransactions, submitFirmForm, updateFirm
} from 'Containers/admin/forms/helpers/firmFormFn';
import { getApiCapailitiesListString } from 'Lib/apiConnectionsHelper';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { formatEpochDate } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getOpenMappedWalletsByFirmId } from 'Selectors/accounts';
import { getApiConnectionsArray } from 'Selectors/apiConnections';
import { getAllAssetsArray } from 'Selectors/assets';
import { getUserAdminRoleId } from 'Selectors/common';
import { getApiTypesByFirmId } from 'Selectors/firmApiTypes';
import { getFirmAssetEntitlementsByFirmId } from 'Selectors/firmAssetEntitlements';
import { getFirmsArray } from 'Selectors/firms';
import { getAllUserApiTypesByFirmIdArray } from 'Selectors/userApiTypes';
import { getUsersByFirmId } from 'Selectors/users';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import type { FirmDetailsPanelProps } from '.';

export function FirmDetailsPanel (props: FirmDetailsPanelProps) {
    const {
        classes,
        firm,
        firmAssetStats
    } = props;

    const userRoleId = useAppSelector(getUserAdminRoleId);
    const apiConnections = useAppSelector(getApiConnectionsArray);
    const firms = useAppSelector((state) => getFirmsArray(state, '', true));
    const firmUsers = useAppSelector((state) => getUsersByFirmId(state, undefined, false, undefined, firm.FirmId));
    const firmAccounts = useAppSelector((state) => getOpenMappedWalletsByFirmId(state, firm?.FirmId));
    const firmSecondaryApiConnections = useAppSelector((state) => getApiTypesByFirmId(state, firm.FirmId, false));
    const firmAllApiTypeUsers = useAppSelector((state) => getAllUserApiTypesByFirmIdArray(state, false, firm.FirmId));
    const availableAssets = useAppSelector(getAllAssetsArray);
    const firmAssetEntitlements = useAppSelector((state) => getFirmAssetEntitlementsByFirmId(state, firm.FirmId));
    const firmCurrentTickers = React.useMemo(() => (
        firmAssetEntitlements.map((entitlement) => entitlement.Ticker)
    ), [firmAssetEntitlements]);
    const firmCurrentEntitledAssets = React.useMemo(() => (
        firmCurrentTickers.map((ticker) => ({ EntitledAssetsTicker: ticker }))
    ), [firmCurrentTickers]);
    const countryName = React.useMemo(() => (
        getCountryOptions()
            .find((option) => option.value === firm.CountryCode)?.label
    ), [firm.CountryCode]);
    const stateProvinceName = React.useMemo(() => (
        getStateProvinceOptions(firm.CountryCode)
            .find((option) => option.value === firm.StateCode)?.label
    ), [ firm.CountryCode, firm.StateCode ]);
    const [features] = useFeatures();
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] = React.useReducer(
        ConfirmationWorkflowReducer,
        DEFAULT_INITIAL_STATE
    );

    // TODO Update to handle multiple API Secondary Connections
    const secondaryApiConnectionExists = apiConnections?.some((api) => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS);

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);
    const [ editFirmModalOpen, setEditFirmModalOpen ] = React.useState<boolean>(false);
    const closeEditFirmModal = () => setEditFirmModalOpen(false);

    const lockFirmValidation = (firm: FirmRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: firm,
                actionType: firm.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${firm.IsLocked ? 'Unlocking' : 'Locking'} Firm ${firm.Name}`,
                confirmBody: `Please confirm ${firm.IsLocked ? 'unlocking' : 'locking'} user functionality for ${firm.Name}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockFirm = async (firm: FirmRecord) => {
        const sendValues: Omit<
            CustomerModifyFirmReq,
            'RequestId' |
            'RequesterId' |
            'SuppressUserEmail' |
            'IsDisabled' |
            'UUID' |
            'FirmId' |
            'APISecondaryConnectionType' |
            'IsSecondaryApiEnabled'
        > = {
            Name: firm.Name,
            FirmEmail: firm.Email,
            Notes: firm.Notes,
            Lei: firm.Lei,
            CompanyCode: firm.CompanyCode,
            IsTokenAPIFirm: firm.IsTokenAPIFirm,
            IsSCFirm: firm.IsSCFirm,
            IsLiquidityFacility: firm.IsLiquidityFacility,
            IsCollateral: firm.IsCollateral,
            IsClosed: firm.IsClosed,
            IsFrozen: firm.IsFrozen,
            IsLocked: !firm.IsLocked,
            CountryCode: firm.CountryCode,
            StateCode: firm.StateCode,
            EntitledAssets: firmCurrentEntitledAssets
        };

        try {
            const response = await Worker.getInstance()
                .updateFirm(firm.FirmId, sendValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${firm.IsLocked ? 'Unlocked' : 'Locked'} ${firm.Name}`,
                        successBody: `Successfully ${firm.IsLocked ? 'unlocked' : 'locked'} user functionality for ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${firm.IsLocked ? 'Unlock' : 'Lock'} ${firm.Name}`,
                        failureBody: `Could not ${firm.IsLocked ? 'unlock' : 'lock'} account functionality for ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${firm.IsLocked ? 'Unlock' : 'Lock'} ${firm.Name}`,
                    failureBody: `Something went wrong ${firm.IsLocked ? 'unlocking' : 'locking'} account functionality for ${firm.Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const freezeFirmValidation = (firm: FirmRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: firm,
                actionType: firm.IsFrozen ? ActionTypes.unfreeze : ActionTypes.freeze,
                confirmTitle: `Confirm ${firm.IsFrozen ? 'Unfreezing' : 'Freezing'} Firm ${firm.Name}`,
                confirmBody: `Please confirm ${firm.IsFrozen ? 'unfreezing' : 'freezing'} user functionality for ${firm.Name}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const freezeFirm = async (firm: FirmRecord) => {
        const sendValues: Omit<
            CustomerModifyFirmReq,
            'RequestId' |
            'RequesterId' |
            'SuppressUserEmail' |
            'IsDisabled' |
            'UUID' |
            'FirmId' |
            'APISecondaryConnectionType' |
            'IsSecondaryApiEnabled'
        > = {
            Name: firm.Name,
            FirmEmail: firm.Email,
            Notes: firm.Notes,
            Lei: firm.Lei,
            CompanyCode: firm.CompanyCode,
            IsTokenAPIFirm: firm.IsTokenAPIFirm,
            IsSCFirm: firm.IsSCFirm,
            IsLiquidityFacility: firm.IsLiquidityFacility,
            IsCollateral: firm.IsCollateral,
            IsClosed: firm.IsClosed,
            IsLocked: firm.IsLocked,
            IsFrozen: !firm.IsFrozen,
            CountryCode: firm.CountryCode,
            StateCode: firm.StateCode,
            EntitledAssets: firmCurrentEntitledAssets
        };

        try {
            const response = await Worker.getInstance()
                .updateFirm(firm.FirmId, sendValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${firm.IsFrozen ? 'Unfroze' : 'Froze'} ${firm.Name}`,
                        successBody: `Successfully ${firm.IsFrozen ? 'unfroze' : 'froze'} user functionality for ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${firm.IsLocked ? 'Unfreeze' : 'Freeze'} ${firm.Name}`,
                        failureBody: `Could not ${firm.IsFrozen ? 'unfreeze' : 'freeze'} user functionality for ${firm.Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${firm.IsFrozen ? 'Unfreeze' : 'Freeze'} ${firm.Name}`,
                    failureBody: `Something went wrong ${firm.IsFrozen ? 'unfreezing' : 'freezing'} user functionality for ${firm.Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockFirmValidation,
                    [ActionTypes.unlock]: lockFirmValidation,
                    [ActionTypes.freeze]: freezeFirmValidation,
                    [ActionTypes.unfreeze]: freezeFirmValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockFirm,
                    [ActionTypes.unlock]: lockFirm,
                    [ActionTypes.freeze]: freezeFirm,
                    [ActionTypes.unfreeze]: freezeFirm
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='row'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={9}
                >
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_firmName_label'
                            variant='body2'
                        >
                            Firm Name
                        </Typography>
                        <Typography
                            className={`${classes.detailText} ${classes.boldText}`}
                            id='firmDetails_firmName_value'
                            variant='body1'
                        >
                            {firm.Name}
                        </Typography>
                        <Grid
                            alignItems='center'
                            container
                            direction='row'
                            spacing={0}
                            wrap='nowrap'
                        >
                            {firm.IsLocked && (
                                <LockIcon
                                    className={classes.statusIcon}
                                    iconColor={LOCK_ICON_COLOR}
                                    id='firmDetails_lockedFirmStatus'
                                />
                            )}
                            {firm.IsFrozen && (
                                <WalletIcon
                                    className={classes.statusIcon}
                                    iconColor={LOCK_ICON_COLOR}
                                    id='firmDetails_frozenFirmStatus'
                                />
                            )}
                        </Grid>
                    </Grid>
                    {features.isSSOEnabled && (
                        <Grid
                            className={classes.detailContainer}
                            item
                        >
                            <Typography
                                className={classes.detailLabel}
                                id='firmDetails_companyCode_label'
                                variant='body2'
                            >
                                Company Code
                            </Typography>
                            <Typography
                                className={classes.detailText}
                                id='firmDetails_companyCode_value'
                                variant='body1'
                            >
                                {firm.CompanyCode}
                            </Typography>
                        </Grid>
                    )}
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_firmId_label'
                            variant='body2'
                        >
                            Firm ID
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='firmDetails_firmId_value'
                            variant='body1'
                        >
                            {firm.FirmId}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_firmEmail_label'
                            variant='body2'
                        >
                            Notification Email
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='firmDetails_firmEmail_value'
                            variant='body1'
                        >
                            {firm.Email}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_firmEmail_label'
                            variant='body2'
                        >
                            Legal Entity Identifier
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='firmDetails_firmEmail_value'
                            variant='body1'
                        >
                            {firm.Lei}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='api_capabilities_label'
                            variant='body2'
                        >
                            API Capabilities
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='api_capabilities_value'
                            variant='body1'
                        >
                            {getApiCapailitiesListString({
                                tokenApi: firm.IsTokenAPIFirm,
                                secondaryApiList: firmSecondaryApiConnections
                            })}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_firmNotes_label'
                            variant='body2'
                        >
                            Notes
                        </Typography>
                        <Typography
                            className={`${classes.detailText} ${!firm.Notes && classes.noNotes}`}
                            id='firmDetails_firmNotes_value'
                            variant='body1'
                        >
                            {firm.Notes || 'No notes'}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_country_label'
                            variant='body2'
                        >
                            Country
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='firmDetails_country_value'
                            variant='body1'
                        >
                            {countryName || '-'}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.detailLabel}
                            id='firmDetails_stateProvince_label'
                            variant='body2'
                        >
                            State/Province
                        </Typography>
                        <Typography
                            className={classes.detailText}
                            id='firmDetails_stateProvince_value'
                            variant='body1'
                        >
                            {stateProvinceName || '-'}
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        container
                        direction='column'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Grid
                            container
                            direction='column'
                            item
                            spacing={0}
                            xs={12}
                        >
                            <Typography
                                className={classes.detailLabel}
                                id='firmDetails_assetPermissions_label'
                                variant='body2'
                            >
                                Asset Permissions
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            direction='row'
                            item
                            spacing={0}
                            xs={12}
                        >
                            {firmCurrentTickers.length > 0 ?
                                firmCurrentTickers.map((ticker) => (
                                    <AssetSymbol
                                        attribute={ticker}
                                        className={classes.assetTag}
                                        id='firmDetails_assetPermissions_value'
                                        key={ticker}
                                    />
                                ))
                                : (
                                    <Typography
                                        className={classes.detailText}
                                        id='firmDetails_assetPermissions_value'
                                        variant='body1'
                                    >
                                        -
                                    </Typography>
                                )}
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.detailContainer}
                        item
                    >
                        <Typography
                            className={classes.createdText}
                            id='firmDetails_firmCreatedAt_value'
                            variant='body2'
                        >
                            Created {formatEpochDate(firm.CreatedAt)}
                        </Typography>
                        {firm.IsClosed && firm.ClosedAt && firm.ClosedAt !== '0' && (
                            <Typography
                                className={classes.createdText}
                                id='firmDetails_firmClosedAt_value'
                                variant='body2'
                            >
                                Closed {formatEpochDate(firm.ClosedAt)}
                            </Typography>
                        )}
                        {!firm.IsClosed && firm.ReopenAt && firm.ReopenAt !== '0' && (
                            <Typography
                                className={classes.createdText}
                                id='firmDetails_firmReopenedAt_value'
                                variant='body2'
                            >
                                Reopened {formatEpochDate(firm.ReopenAt)}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Grid
                    alignItems='flex-end'
                    container
                    direction='column'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                    xs={3}
                >
                    <Grid
                        className={classes.firmActionButtonsContainer}
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        {firm.IsClosed && (
                            <ClosedSymbol
                                className={classes.closedTag}
                                id='firmDetails_firmClosedStatus'
                            />
                        )}
                        {userRoleId !== AdminRoleId.BANK_OPS_SUPERVISOR && userRoleId !== AdminRoleId.PCG_ADMIN && (
                            <>
                                <ActionButton
                                    className={classes.actionButton}
                                    disabled={firm.IsClosed}
                                    handleClick={() => freezeFirmValidation(firm)}
                                    Icon={WalletIcon}
                                    id='firmDetails_freezeFirmButton'
                                    text={firm.IsFrozen ? 'Unfreeze' : 'Freeze'}
                                    type='icon'
                                />
                                <ActionButton
                                    className={classes.actionButton}
                                    disabled={firm.IsClosed}
                                    handleClick={() => lockFirmValidation(firm)}
                                    Icon={firm.IsLocked ? UnlockIcon : LockIcon}
                                    id='firmDetails_lockFirmButton'
                                    text={firm.IsLocked ? 'Unlock' : 'Lock'}
                                    type='icon'
                                />
                                <ActionButton
                                    handleClick={() => setEditFirmModalOpen(true)}
                                    Icon={EditIcon}
                                    id='firmDetails_editFirmButton'
                                    text='Edit'
                                    type='icon'
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <FormModal
                Form={FirmForm}
                formProps={{
                    actionType: ActionTypes.edit,
                    secondaryApiConnectionExists,
                    firmSecondaryApiConnections,
                    editFirm: firm,
                    submitFirmForm: (values, opts) => submitFirmForm(values, opts),
                    getTransactions: () => getTransactions(firm.FirmId),
                    getCollateral: () => getCollateral(firm.FirmId),
                    updateFirm: updateFirm,
                    firmAssetStats: firmAssetStats,
                    firms,
                    firmUsers,
                    firmEoaAccounts: firmAccounts,
                    userAccounts: [],
                    features: {
                        ...features,
                        ...features['authentication']
                    },
                    firmAllApiTypeUsers,
                    availableAssets,
                    firmAssetEntitlements
                }}
                modalWidth='744px'
                onClose={closeEditFirmModal}
                open={editFirmModalOpen}
                title='Edit Firm'
            />
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}